import {
    Hydrate,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import React, { useEffect, useState } from 'react'
import 'react-loading-skeleton/dist/skeleton.css'

import {
    GlobalFooter,
    GlobalNavigation,
    getStateVariant,
} from '@mediaingenuity/totallymoney.globalassets'
import { captureException, setUser } from '@sentry/nextjs'
import type { AppProps } from 'next/app'
import { ThemeProvider } from 'styled-components'

import totallyMoneyTheme from '@totallymoney/ui/theme/TotallyMoneyTheme'

import { MockQueryUnion } from '@api/mocks/get-scored-categories'

import DevToolBanner from '@components/DevToolBanner/DevToolBanner'
import DevToolBannerInfo from '@components/DevToolBannerInfo'
import DynamicDevTools from '@components/DevTools/DynamicDevTools'
import GlobalFlashMessage from '@components/GlobalFlashMessage'
import LogVisitEvent from '@components/LogEvents'

import { BasinProvider } from '@context/BasinContext'
import { FeatureFlagProvider } from '@context/FeatureFlagContext'

import { getAuthErrorInfo } from '@utils/getAuthErrorInfo'
import { getAuthRedirectUrl } from '@utils/getAuthRedirectUrl'

import GlobalStyle from '../styles/GlobalStyles'

export default function MyApp({ Component, pageProps, router }: AppProps) {
    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    mutations: {
                        onError: error => {
                            captureException(error)
                        },
                    },
                    queries: {
                        onError: error => {
                            captureException(error)

                            if (
                                typeof window !== 'undefined' &&
                                getAuthErrorInfo(error) != null
                            ) {
                                window.location.href = getAuthRedirectUrl()
                            }
                        },
                    },
                },
            })
    )

    const campaignPages = ['signup', 'welcome']

    const variant = getStateVariant()
    const hideNavigation = router.query.hidenavigation
    const environment =
        process.env.NEXT_PUBLIC_APP_ENV === 'development' ||
        process.env.NEXT_PUBLIC_APP_ENV == 'test'
            ? 'staging'
            : process.env.NEXT_PUBLIC_APP_ENV

    useEffect(() => {
        if (router.query.customerId != null) {
            const customerId = router.query.customerId as string

            setUser({
                username: customerId,
                id: customerId,
            })
        }
    }, [router.query.customerId])

    const isCampaignLandingPage = campaignPages.some(campaignName =>
        router.pathname.includes(campaignName)
    )

    return (
        <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
                <BasinProvider environment={environment}>
                    <FeatureFlagProvider>
                        <ThemeProvider theme={totallyMoneyTheme}>
                            <GlobalStyle />
                            <LogVisitEvent />

                            {process.env.NODE_ENV === 'development' ? (
                                <DevToolBanner />
                            ) : null}

                            {process.env.NODE_ENV === 'development' ? (
                                <DevToolBannerInfo
                                    queryScoreId={
                                        router?.query?.[
                                            'scoreId'
                                        ] as MockQueryUnion
                                    }
                                />
                            ) : null}

                            <main>
                                {isCampaignLandingPage ? (
                                    <GlobalNavigation
                                        environment={environment}
                                        shouldShowNavigationLinks={false}
                                    />
                                ) : null}

                                {!hideNavigation && !isCampaignLandingPage ? (
                                    <GlobalNavigation
                                        environment={environment}
                                    />
                                ) : null}

                                <GlobalFlashMessage />

                                {router.query.devtools === 'true' ? (
                                    <DynamicDevTools />
                                ) : null}
                                <Component {...pageProps} />

                                {isCampaignLandingPage ? (
                                    <GlobalFooter
                                        environment={environment}
                                        defaultVariant="landingPage"
                                    />
                                ) : (
                                    <GlobalFooter
                                        environment={environment}
                                        defaultVariant={variant}
                                    />
                                )}
                            </main>
                        </ThemeProvider>
                    </FeatureFlagProvider>
                </BasinProvider>
            </Hydrate>
            <ReactQueryDevtools />
        </QueryClientProvider>
    )
}
